import React, { useState } from "react";
import {
  Button,
  Modal,
  Box,
  IconButton,
  Typography,
  Card,
  CardContent,
  Grid
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import urlconfig from "../config/urlconfig";
import { formatCurrency } from "../UserInput";
import { useAuth } from "../AuthProvider";
import { usePermissions } from "../rbac/PermissionsProvider";
import { PERMISSIONS } from "../rbac/permissions";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  height: "95%",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  display: "flex",
  flexDirection: "column",
};

const contentStyle = {
  overflowY: "auto",
  flex: 1,
  p: 2,
};

const footerStyle = {
  p: 2,
  display: "flex",
  justifyContent: "space-between",
  background: "white",
  borderTop: "1px solid #eee",
  boxShadow: "0 -4px 16px rgba(0,0,0,0.1)",
};

// `row` is a Purchase dashboard row; the full cost sheet is loaded when the dialog opens
export default function PurchaseInvoiceModal({ row, getApiToken }) {
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState(null);
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const { username } = useAuth();
  
  const { can } = usePermissions();
  const DeleteAccess = can(PERMISSIONS.INVOICE_DELETE);

  const handleOpen = async () => {
    try {
      const token = await getApiToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [costSheetResponse, response] = await Promise.all([
        fetch(`${urlconfig.backend_api_base_url}/api/v2/items/${encodeURIComponent(row.id)}`, { headers }),
        fetch(
          `${urlconfig.backend_api_base_url}/api/v2/bill/${row.id}/invoices?soNumber=${row.salesOrderNo}`,
          { headers }
        ),
      ]);
      if (!costSheetResponse.ok) throw new Error(`HTTP ${costSheetResponse.status}`);
      setItem(await costSheetResponse.json());

      const data = await response.json();
      setInvoiceFiles(data.files || []);
      setOpen(true);
    } catch (err) {
      alert("Failed to load invoices: " + err.message);
    }
  };

  const formatIST = (dateStr) => {
    if (!dateStr) return "No Date";
    return new Date(dateStr).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const handleDeletePurchasePO = async (invoiceId) => {
  const token = await getApiToken();

  await fetch(
    `${urlconfig.backend_api_base_url}/api/v2/bill/delete-purchase-order?invoiceId=${invoiceId}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  setInvoiceFiles(prev =>
    prev.map(f =>
      f.id === invoiceId ? { ...f, poBillUrl: null } : f
    )
  );
};

const handleDeletePurchasePI = async (invoiceId) => {
  const token = await getApiToken();

  await fetch(
    `${urlconfig.backend_api_base_url}/api/v2/bill/delete-purchase-invoice?invoiceId=${invoiceId}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  setInvoiceFiles(prev =>
    prev.map(f =>
      f.id === invoiceId
        ? {
            ...f,
            invoiceFileUrl: null,
            invoiceNo: null,
            purchaseInvoiceAmount: null,
          }
        : f
    )
  );
};

const handleConfirmDelete = (type, id) => {
  if (!window.confirm("Are you sure you want to delete this item?")) return;

  if (type === "PO") handleDeletePurchasePO(id);
  if (type === "PI") handleDeletePurchasePI(id);
};


  // Renders PDF in iframe, images in <img>
  const renderFile = (url, alt) => {
    if (!url) return <Typography variant="subtitle2" color="error" >File not found or it may have been deleted</Typography>;


    const isPDF = url.split("?")[0].toLowerCase().endsWith(".pdf");
    const mediumHeight = 300;

    return isPDF ? (
      <iframe
        src={url}
        style={{ width: "100%", height: mediumHeight, border: "1px solid #ddd" }}
        title={alt}
      />
    ) : (
      <img
        src={url}
        alt={alt}
        style={{
          width: "100%",
          maxHeight: mediumHeight,
          border: "1px solid #ddd",
          objectFit: "contain",
        }}
      />
    );


  };


      const actualPurchaseInvoiceAmount = item ? (() => {
      const tPp  = Number(item.transportationTotalPp || 0);
      const cPp  = Number(item.customizationTotalPp  || 0);
      const mPp  = Number(item.marketingTotalPp       || 0);
      const tGst = Number(item.costSummary?.transportationGST || 0);
      const cGst = Number(item.costSummary?.customizationGST  || 0);
      const mGst = Number(item.costSummary?.marketingGST      || 0);
      const itemPpTotal = item.items?.reduce((sum, i) => {
        const pp = Number(i.pp || 0), gst = Number(i.gst || 0), qty = Number(i.qty || 0);
        return sum + (pp + (pp * gst) / 100) * qty;
      }, 0) || 0;
      const customPpWithGst = (item.customRows || []).reduce((s, r) => {
        const pp = parseFloat(r.customTotalPp) || 0, gst = parseFloat(r.customGST) || 0;
        return s + pp * (1 + gst / 100);
      }, 0);
      return itemPpTotal + tPp * (1 + tGst / 100) + cPp * (1 + cGst / 100) + mPp * (1 + mGst / 100) + customPpWithGst;
    })() : 0;


  const transportationActualCost =
  item?.transportationDetails?.reduce(
    (sum, t) => sum + Number(t.actualTransportationCost || 0),
    0
  ) || 0;
  

 

    const customizationActualCost =
  item?.customizationDetails?.reduce(
    (sum, c) => sum + Number(c.actualCustomizationCost || 0),
    0
  ) || 0;

  const totalPIAmount =
    invoiceFiles?.reduce((sum, i) => sum + Number(i.purchaseInvoiceAmount || 0), 0) + transportationActualCost + customizationActualCost || 0;


  const renderFileCard = (file, index) => (
      <Card key={index} sx={{ mb: 2, borderRadius: 2, boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>
        <CardContent>
           <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}>
        <Typography variant="caption" fontWeight={600}>
          #{index + 1}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          Uploaded: {formatIST(file.uploadedAt)}
        </Typography>
      </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                PO Number: {file.poNumber || "N/A"}
              </Typography>
              <Typography variant="body2" fontWeight={500} mt={0.5} mb={0.5}>
                Purchase Order:
              </Typography>
              {renderFile(file.poBillUrl, `PO Bill ${file.poNumber || index}`)}
              {file.poBillUrl && DeleteAccess && (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => handleConfirmDelete("PO", file.id)}
                  >
                    Delete PO
                  </Button>
                )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                Purchase Invoice #: {file.invoiceNo || "N/A"}  | Value : ₹{formatCurrency(file.purchaseInvoiceAmount) || "N/A"}
              </Typography>
              <Typography variant="body2" fontWeight={500} mt={0.5} mb={0.5}>
                Invoice File:
              </Typography>
              {renderFile(file.invoiceFileUrl, `Invoice File ${file.invoiceNo || index}`)}
              {file.invoiceFileUrl && DeleteAccess && (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => handleConfirmDelete("PI", file.id)}
                  >
                    Delete PI
                  </Button>
                )}
            </Grid>
          </Grid>

        </CardContent>
      </Card>
        );


  return (
    <>
      <Button
        color="info"
        variant="outlined"
        onClick={handleOpen}
        sx={{ fontSize: "14px", width: "100%" }}
      >
        View PO & PI </Button>


      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={modalStyle}>
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" fontWeight="600" mb={1} sx={{ p: 1}}>
            View PO & PI
          </Typography>

<Box sx={contentStyle}>            
            {invoiceFiles.length > 0
              ?  [...invoiceFiles]
                .sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))
                .map((file, index) => renderFileCard(file, index))
              : <Typography>No invoice files found.</Typography>}

           <Grid container my={2}>
                      <Grid item md={6}>
                              <Box
                                sx={{
                                  mt: 2,
                                  mx:2,
                                  p: 2,
                                  borderRadius: 3,
                                  background: "linear-gradient(135deg, #f5f7fa, #e4e7eb)",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                }}
                              >
                                <Typography variant="h6" fontWeight="bold" mb={2}>
                                  Transportation Cost Summary
                                </Typography>
          
                                <Grid container spacing={2}>
                                  {/* Budgeted */}
                                  <Grid item xs={12} sm={4}>
                                    <Box
                                      sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        backgroundColor: "#ffffff",
                                        textAlign: "center",
                                        height:"75px"
                                      }}
                                    >
                                      <Typography variant="caption" color="text.secondary">
                                        Budgeted Transportation Cost
                                      </Typography>
                                      <Typography variant="h6" fontWeight="bold">
                                       {(() => {
                                            const pp = Number(item?.transportationTotalPp || 0);
                                            const gst = Number(item?.costSummary?.transportationGST || 0);
                                            return `₹${formatCurrency(pp * (1 + gst / 100))}`;
                                          })()}
                                      </Typography>
                                    </Box>
                                  </Grid>
          
                                  {/* Actual */}
                                  <Grid item xs={12} sm={4}>
                                    <Box
                                      sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        backgroundColor: "#ffffff",
                                        textAlign: "center",
                                        height:"75px"
                                      }}
                                    >
                                      <Typography variant="caption" color="text.secondary">
                                        Actual Transportation Cost
                                      </Typography>
                                      <Typography variant="h6" fontWeight="bold">
                                        ₹{formatCurrency(
                                          item?.transportationDetails?.reduce(
                                            (sum, item) => sum + Number(item.actualTransportationCost || 0),
                                            0
                                          ) || 0
                                        )}
                                      </Typography>
                                    </Box>
                                  </Grid>
          
                                  {/* Variance */}
                                  <Grid item xs={12} sm={4}>
                                    {(() => {
                                      const tPp = Number(item?.transportationTotalPp || 0);
                                      // const budget = Number(item?.transportationTotalPp || 0);
                                      const budget = tPp * (1 + Number(item?.costSummary?.transportationGST || 0) / 100);

                                      const actual =
                                        item?.transportationDetails?.reduce(
                                          (sum, item) => sum + Number(item.actualTransportationCost || 0),
                                          0
                                        ) || 0;
                                      const variance = budget - actual;
          
                                      return (
                                        <Box
                                          sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: variance >= 0 ? "#e8f5e9" : "#fdecea",
                                            textAlign: "center",
                                            height:"75px"
                                          }}
                                        >
                                          <Typography variant="caption" color="text.secondary">
                                            Variance
                                          </Typography>
                                          <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            color={variance >= 0 ? "success.main" : "error.main"}
                                          >
                                            ₹{formatCurrency(variance)}
                                          </Typography>
                                        </Box>
                                      );
                                    })()}
                                  </Grid>
                                </Grid>
                              </Box>
                        </Grid>
          
          
          
                      <Grid item md={6}>
                        <Box
                          sx={{
                            mt: 2,
                            mx:2,
                            p: 2,
                            borderRadius: 3,
                            background: "linear-gradient(135deg, #f5f7fa, #e4e7eb)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                          }}
                        >
                          <Typography variant="h6" fontWeight="bold" mb={2}>
                            Customization Cost Summary
                          </Typography>
          
                          <Grid container spacing={2}>
                            {/* Budgeted */}
                            <Grid item xs={12} sm={4}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  backgroundColor: "#ffffff",
                                  textAlign: "center",
                                  height:"75px"
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  Budgeted Customization Cost
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                  {/* ₹{formatCurrency(Number(item?.customizationTotalPp || 0))} */}
                                  {(() => {
                                      const pp = Number(item?.customizationTotalPp || 0);
                                      const gst = Number(item?.costSummary?.customizationGST || 0);
                                      return `₹${formatCurrency(pp * (1 + gst / 100))}`;
                                    })()}
                                </Typography>
                              </Box>
                            </Grid>
          
                            {/* Actual */}
                            <Grid item xs={12} sm={4}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  backgroundColor: "#ffffff",
                                  textAlign: "center",
                                  height:"75px"
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  Actual Customization Cost
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                  ₹{formatCurrency(
                                    item?.customizationDetails?.reduce(
                                      (sum, item) => sum + Number(item.actualCustomizationCost || 0),
                                      0
                                    ) || 0
                                  )}
                                </Typography>
                              </Box>
                            </Grid>
          
                            {/* Variance */}
                            <Grid item xs={12} sm={4}>
                              {(() => {
                                // const budget = Number(item?.customizationTotalPp || 0);
                                const cPp = Number(item?.customizationTotalPp || 0);
                                const budget = cPp * (1 + Number(item?.costSummary?.customizationGST || 0) / 100);
                                const actual =
                                  item?.customizationDetails?.reduce(
                                    (sum, item) => sum + Number(item.actualCustomizationCost || 0),
                                    0
                                  ) || 0;
                                const variance = budget - actual;
          
                                return (
                                  <Box
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      backgroundColor: variance >= 0 ? "#e8f5e9" : "#fdecea",
                                      textAlign: "center",
                                      height:"75px"
                                    }}
                                  >
                                    <Typography variant="caption" color="text.secondary">
                                      Variance
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      fontWeight="bold"
                                      color={variance >= 0 ? "success.main" : "error.main"}
                                    >
                                      ₹{formatCurrency(variance)}
                                    </Typography>
                                  </Box>
                                );
                              })()}
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>

          </Box>

          
          {/* Footer */}
          <Box sx={footerStyle}>
            <Typography fontWeight="bold" fontSize="18px">
              PO Value: ₹{formatCurrency(actualPurchaseInvoiceAmount)}
              <br></br>
              <small variant="subtitle2" color="secondary" p={0}>* Includes Budgeted Transportation  & Customization cost</small>
            </Typography>

            <Typography fontWeight="bold" fontSize="18px">
              PI Value: ₹{formatCurrency(totalPIAmount)}
              <br></br>
              <small variant="subtitle2" color="secondary" p={0}>* Includes Actual Transportation  & Customization cost</small>
            </Typography>

            <Typography
              fontWeight="bold"
              fontSize="18px"
              sx={{ color: actualPurchaseInvoiceAmount - totalPIAmount < 0 ? "Green" : "red" }}
            >
              Balance: ₹{formatCurrency(actualPurchaseInvoiceAmount - totalPIAmount)}
            </Typography>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
